import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { MobileHeaderNotificationBell } from "@/modules/notifications/components/MobileHeaderNotificationBell";

// Pixels of scroll before the compact bar collapses in.
const COLLAPSE_PX = 56;

// ---------------------------------------------------------------------------
// MobileScreenHeader — large-title + scroll-collapse for primary screens
// ---------------------------------------------------------------------------

type ScreenHeaderProps = {
  /** Title shown in the compact bar when collapsed. */
  title: string;
  /**
   * Action buttons — declared ONCE. The header renders them in the large
   * title row at rest and in the compact bar when collapsed, marking the
   * inactive copy `inert` so it leaves the tab order and the a11y tree.
   */
  actions?: React.ReactNode;
  /** Optional eyebrow above the heading (large header only). */
  eyebrow?: React.ReactNode;
  /** Large heading row content, e.g. an `<h1>` plus an inline pill. */
  heading?: React.ReactNode;
  /** Optional subtitle below the heading (large header only). */
  subtitle?: React.ReactNode;
  /** aria-label for the large header section. */
  ariaLabel?: string;
  /**
   * Visual theme of the header chrome. "light" (default) is the cream large-
   * title header used across most screens; "dark" is the branded navy header
   * (#0c1118) — consumers using "dark" must pass light-on-dark heading/
   * subtitle/action content.
   */
  theme?: "light" | "dark";
  /**
   * Light-theme background colour (CSS value). Defaults to the app canvas
   * (gray). Pass e.g. `var(--shell)` for a white header that matches the
   * compact page headers (customers/coupons/home). Ignored when theme="dark".
   */
  surface?: string;
  /** Content below the title row: status chips, range segmented, KPIs, etc. */
  children?: React.ReactNode;
};

// Branded dark header palette (matches the mobile design handover).
const DARK_BG = "#0c1118";
const DARK_HAIRLINE = "rgba(255,255,255,0.08)";

export function MobileScreenHeader({
  title,
  actions,
  eyebrow,
  heading,
  subtitle,
  ariaLabel,
  theme = "light",
  surface,
  children,
}: ScreenHeaderProps) {
  const isDark = theme === "dark";
  const lightBg = surface ?? "var(--canvas)";
  // Desktop surfaces the NotificationBell in the AppShell top bar, which is
  // absent on mobile — append it to every screen header's action row so
  // notifications (and the unread badge) stay reachable on phones.
  const actionsRow = (
    <>
      {actions}
      <MobileHeaderNotificationBell theme={theme} />
    </>
  );
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    const el = document.querySelector(
      '[data-scroll-container="portal"]'
    ) as HTMLElement | null;
    return el ? el.scrollTop > COLLAPSE_PX : false;
  });

  const [prefersReducedMotion] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  const dur = prefersReducedMotion ? "0ms" : "200ms";

  useEffect(() => {
    const el = document.querySelector(
      '[data-scroll-container="portal"]'
    ) as HTMLElement | null;
    if (!el) return;
    const onScroll = () => setCollapsed(el.scrollTop > COLLAPSE_PX);
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div>
      {/* Fixed compact bar — OVERLAYS the large header rather than reserving
          space for it. At rest it's transparent and click-through, so the
          large title sits flush at the top (no empty band); when collapsed it
          fades to an opaque bar over the scrolled content. */}
      <div
        className="fixed inset-x-0 top-0 z-50 lg:hidden"
        style={{
          paddingTop: "env(safe-area-inset-top, 0px)",
          backgroundColor: collapsed
            ? isDark
              ? DARK_BG
              : lightBg
            : "transparent",
          pointerEvents: collapsed ? "auto" : "none",
          transition: `background-color ${dur} ease`,
        }}
      >
        <div className="flex h-[52px] items-center px-4">
          <span
            className="min-w-0 flex-1 truncate text-[17px] font-bold"
            style={{
              color: isDark ? "#fff" : "var(--text-primary)",
              opacity: collapsed ? 1 : 0,
              transition: `opacity ${dur} ease`,
            }}
          >
            {title}
          </span>
          {/* Actions: hidden at rest so they don't float above the large title;
              fade in alongside the compact title when collapsed. `inert` while
              hidden keeps this copy out of the tab order and the a11y tree. */}
          <div
            className="flex shrink-0 items-center gap-1"
            inert={!collapsed}
            style={{
              opacity: collapsed ? 1 : 0,
              transition: `opacity ${dur} ease`,
            }}
          >
            {actionsRow}
          </div>
        </div>
        {/* Hairline — fades in on collapse */}
        <div
          className="h-px w-full"
          style={{
            backgroundColor: isDark ? DARK_HAIRLINE : "var(--border-light)",
            opacity: collapsed ? 1 : 0,
            transition: `opacity ${dur} ease`,
          }}
        />
      </div>

      {/* Large header — flows from the very top; only the safe-area inset is
          reserved so the title clears the status bar in standalone PWA. The
          overlaying bar above is transparent at rest, so there's no dead band.
          Title row owns the single actions declaration; when collapsed the bar
          takes over, so this copy goes `inert`. */}
      <section
        className="px-4 pb-1"
        style={{
          backgroundColor: isDark ? DARK_BG : lightBg,
          paddingTop: "calc(env(safe-area-inset-top, 0px) + 8px)",
        }}
        aria-label={ariaLabel}
      >
        {/* min-h reserves the action-button row height so screens with no
            page actions keep the same header height as those with them — no
            vertical jump when switching. The bell renders on every screen. */}
        <div className="mb-3.5 flex min-h-[44px] items-start gap-3">
          <div className="min-w-0 flex-1">
            {eyebrow}
            {heading}
            {subtitle}
          </div>
          <div
            className="flex shrink-0 items-center gap-1.5"
            inert={collapsed}
          >
            {actionsRow}
          </div>
        </div>
        {children}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MobileCompactHeader — persistent compact bar for detail / pushed screens
// ---------------------------------------------------------------------------

type CompactHeaderProps = {
  title: string;
  /** Called on back button tap; defaults to navigate(-1). */
  onBack?: () => void;
  /** Optional action buttons at the end of the bar. */
  actions?: React.ReactNode;
};

export function MobileCompactHeader({
  title,
  onBack,
  actions,
}: CompactHeaderProps) {
  const navigate = useNavigate();
  const t = useTranslations();

  return (
    <>
      <div
        className="fixed inset-x-0 top-0 z-50 bg-[color:var(--canvas)] lg:hidden"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className="flex h-[52px] items-center gap-1 px-2">
          <button
            type="button"
            onClick={onBack ?? (() => navigate(-1))}
            aria-label={t(keys.common.back)}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--surface-alt)]"
          >
            <BackChevronIcon />
          </button>
          <span className="min-w-0 flex-1 truncate px-1 text-[17px] font-bold text-[color:var(--text-primary)]">
            {title}
          </span>
          {actions ? (
            <div className="flex shrink-0 items-center gap-1 pe-2">{actions}</div>
          ) : null}
        </div>
        <div className="h-px w-full bg-[color:var(--border-light)]" />
      </div>
      {/* Spacer to push page content below the fixed bar */}
      <div
        aria-hidden="true"
        style={{ height: "calc(env(safe-area-inset-top, 0px) + 53px)" }}
      />
    </>
  );
}

function BackChevronIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="rtl:rotate-180"
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}
